


















































































































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';

import { shouldHideMonthlyPlansFromPaywall } from 'frontend/experiments/premium_referrals_10_2021';
import {
  Coupon,
  MobileGetter,
  Optional,
  User,
  PaywallPlansInfo,
  PaywallPlanObject,
  ExperimentGetter,
} from 'frontend/types';
import { Discount } from 'frontend/types/premium';
import {
  isLegacyDirectTraffic,
  isLegacySeasonal,
  isStudentDiscount,
} from 'frontend/utils/discounts';
import { safeWindow } from 'frontend/utils/window';
import PaywallPlan from 'frontend/views/premium/PaywallPlan.vue';
import PaywallSubscriptionNotice from 'frontend/views/premium/components/PaywallSubscriptionNotice.vue';
import PaywallPlanMini from 'frontend/views/premium/components/PaywallPlanMini.vue';
import { subscriptionGiftAddOns } from 'frontend/experiments/subscription_add_ons_06_2022';
import { RawLocation } from 'vue-router';
import CourseSubPack from 'frontend/views/premium/paywall/CourseSubPack.vue';
import { isInMathSubpackExperiment } from 'frontend/experiments/math_subpack';

interface SubPackInput {
  plan_id: number;
}

@Component({
  components: {
    CourseSubPack,
    PaywallPlan,
    PaywallPlanMini,
    PaywallSubscriptionNotice,
  },
  computed: {
    ...mapState('premiumPurchaseInfo', ['is_discounted']),
    ...mapState('pageHeader', ['context']),
    ...mapGetters('pageHeader', ['isMobile', 'coupon']),
    ...mapGetters('globals', ['discount', 'inExperiment']),
    ...mapState('globals', ['user']),
    ...mapGetters('paywall', ['paywallPlans']),
    ...mapState('paywall', ['trial_length']),
  },
})
export default class PaywallPlans extends Vue {
  @Prop({ type: Boolean, default: false }) private readonly upgrade!: boolean;

  private readonly paywallPlans!: PaywallPlansInfo;
  private readonly context: any;
  private readonly coupon!: Coupon;
  private readonly user!: User;
  private readonly isMobile!: MobileGetter;
  // tslint:disable-next-line:variable-name
  private readonly is_discounted!: boolean;
  // tslint:disable-next-line:variable-name
  private readonly trial_length!: boolean;
  private readonly discount!: Optional<Discount>;
  private inExperiment!: ExperimentGetter;

  private showLegal = false;

  private get directTrafficDiscount(): boolean {
    return isLegacyDirectTraffic(this.discount);
  }

  private get monthlyPlan(): Optional<PaywallPlanObject> {
    if (shouldHideMonthlyPlansFromPaywall(this.context)) {
      return undefined;
    }
    return this.paywallPlans.monthlyPlan;
  }

  private get seasonalDiscount(): boolean {
    return isLegacySeasonal(this.discount);
  }

  private get isStudentDiscount(): boolean {
    return isStudentDiscount(this.discount);
  }

  private get experimentAwareTrialPlanPriceMessage(): string {
    const asterisk = this.paywallPlans.annualPlanIncludesReferralDiscount ? '*' : '';
    return `${this.paywallPlans?.annualPlan?.display_price}${asterisk}/month after free trial (billed annually)`;
  }

  private axId(interval: string): string {
    if (interval === 'lifetime') {
      return 'clicked_b2_lifetime_upgrade_chargebee';
    }
    const intervalComponent =
      interval === 'annual' ? 'annual' : interval === 'monthly' ? 'monthly' : undefined;

    return `clicked_b2_${intervalComponent}_logged_in_chargebee`;
  }

  private get inMathSubPackExperiment(): boolean {
    return isInMathSubpackExperiment(this.inExperiment);
  }

  private get shouldShowCourseSubPack(): boolean {
    /**
     * For this fake door experiment a user should be shown the math sub pack if:
     * - they are in the experiment
     * - they are a student
     * - the account is older than 48 hours (this is not represented in this if statement because the NUX Paywall
     * is show to all users for the first 48 hours. The PaywallPlans component is only shown to accounts greater than
     * 48 hours)
     * - they are in the US
     * - they have an annual plan available (The subpack will be offered as an annual plan. For this experiment we are
     * showing the user a heavily discounted version of the annual plan. If this experiment is successful and we
     * create the backend, this stipulation will be removed.)
     */

    return (
      this.inMathSubPackExperiment &&
      this.user.user_type === 'student' &&
      this.user.student_country_name === 'USA' &&
      this.paywallPlans.annualPlan !== undefined
    );
  }

  private get inSubscriptionGiftAddOnExperiment(): boolean {
    return subscriptionGiftAddOns(this.inExperiment);
  }

  private giftFormLocation(planType: string): RawLocation {
    return {
      name: 'gift_purchase_message',
      params: { planType },
    };
  }

  private handleAnnualClick() {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else if (this.paywallPlans.annualPlan) {
      if (this.inSubscriptionGiftAddOnExperiment) {
        const location = this.giftFormLocation('annual');
        this.$router.push(location);
      } else {
        const win = safeWindow();
        if (win) {
          (win as any).location.href = this.paywallPlans.annualPlan.checkout_url;
        }
      }
    } else {
      this.safeNav(this.paywallPlans.annualPlan!.checkout_url);
    }
  }

  private handleMonthlyClick(): void {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else if (this.monthlyPlan) {
      if (this.inSubscriptionGiftAddOnExperiment) {
        const location = this.giftFormLocation('annual');
        this.$router.push(location);
      } else {
        const win = safeWindow();
        if (win) {
          (win as any).location.href = this.monthlyPlan.checkout_url;
        }
      }
    } else {
      this.safeNav(this.monthlyPlan!.checkout_url);
    }
  }

  private handleLifetimeClick() {
    if (this.paywallPlans.lifetimePlan) {
      if (this.inSubscriptionGiftAddOnExperiment) {
        const location = this.giftFormLocation('annual');
        this.$router.push(location);
      } else {
        const win = safeWindow();
        if (win) {
          (win as any).location.href = this.paywallPlans.lifetimePlan.checkout_url;
        }
      }
    } else {
      this.safeNav(this.paywallPlans.lifetimePlan!.checkout_url);
    }
  }

  private handleSubscribeNowClick(): void {
    if (!this.user.is_authenticated) {
      this.$store.dispatch('signupModal/signup');
    } else {
      if (this.inSubscriptionGiftAddOnExperiment) {
        const location = this.giftFormLocation('annual');
        this.$router.push(location);
      }
      this.safeNav(this.paywallPlans.annualPlanDirect!.checkout_url);
    }
  }

  private safeNav(url: string): void {
    const safeWin = safeWindow();
    if (safeWin) {
      safeWin.location.href = url;
    }
  }

  private get strikeThroughPrice(): string | null {
    if (
      !this.directTrafficDiscount ||
      this.discount?.is_visible ||
      this.paywallPlans.annualPlanIncludesReferralDiscount
    ) {
      // fallback to usual behavior
      if (this.paywallPlans.annualPlan && this.paywallPlans.annualPlan.strikethrough_price) {
        return this.paywallPlans.annualPlan.strikethrough_price;
      }
    }
    return null;
  }

  private get shouldShowDiscount(): boolean {
    // Referral discount takes precedence
    if (this.paywallPlans.annualPlanIncludesReferralDiscount) {
      return true;
    }
    if (this.seasonalDiscount) {
      return true;
    }
    if (this.directTrafficDiscount) {
      return !!this.discount?.is_visible;
    }
    if (this.isStudentDiscount) {
      return true;
    }
    return !!(
      this.is_discounted ||
      (this.paywallPlans.annualPlan && this.paywallPlans.annualPlan.strikethrough_price)
    );
  }

  private get campaignDiscountOverrideHtml(): string | null {
    if (this.seasonalDiscount || this.isStudentDiscount) {
      return `${this.discount?.name} discount applied`;
    }
    if (this.directTrafficDiscount) {
      return 'Limited time discount applied';
    }
    return null;
  }

  private get discountHtml(): string | null {
    if (this.paywallPlans.annualPlanIncludesReferralDiscount) {
      return 'Referral discount unlocked!';
    }
    if (this.campaignDiscountOverrideHtml) {
      return this.campaignDiscountOverrideHtml;
    }
    if (this.coupon && this.coupon.code_used_name) {
      return `Discount unlocked for <em>${this.coupon.code_used_name}</em>`;
    }
    if (this.user.days_since_signup !== null && this.user.days_since_signup <= 7) {
      return 'Discount unlocked! Thanks for joining Brilliant.';
    }
    return 'Discount unlocked! Thanks for being active on Brilliant.';
  }

  private get isMobileViewPort(): boolean {
    return this.isMobile(this.$mq);
  }

  private get showOnePaymentWarning(): boolean {
    // during trial
    if (this.user.is_during_trial) {
      return false;
    }
    if (this.paywallPlans.annualPlanIncludesReferralDiscount) {
      return true;
    }
    // trial paywall
    if (this.paywallPlans.annualPlanIsTrialPlan) {
      return false;
    }
    // paywall ONLY includes a monthly plan
    return !(
      !this.paywallPlans.annualPlan &&
      !this.paywallPlans.threeMonthPlan &&
      !this.paywallPlans.sixMonthPlan &&
      !this.paywallPlans.twoYearPlan &&
      !this.paywallPlans.threeYearPlan &&
      !this.paywallPlans.fourYearPlan
    );
  }

  private get onePaymentWarning(): string {
    if (this.paywallPlans.annualPlanIncludesReferralDiscount) {
      return this.referralDiscountActiveOnePaymentWarning;
    }
    return `*Billed as one payment. Subscription renews automatically at the end of term.
        You can turn off auto-renew from your settings. Learn more`;
  }

  private get referralDiscountActiveOnePaymentWarning(): string {
    const intervalMessage = '/month';

    const commonMessage = `*For the first year,
        then ${this.paywallPlans?.annualPlan?.renewal_price}${intervalMessage};
        all terms are billed as one payment.`;

    const remainingMessage = this.paywallPlans.annualPlanIsTrialPlan
      ? ''
      : ` Subscription renews automatically at the end of term. You can turn off auto-renew
            from your settings. Learn more`;

    return `${commonMessage}${remainingMessage}`;
  }
}
